import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  Avatar,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { toast } from "react-toastify";
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";

export default function StaffSalaryAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [salaryData, setSalaryData] = useState([]);
  
  // Get current month and year - always start fresh on page load
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState((currentDate.getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear().toString());

  // Clear localStorage on mount to always start with current month
  useEffect(() => {
    localStorage.removeItem('staffSalarySelectedMonth');
    localStorage.removeItem('staffSalarySelectedYear');
  }, []);

  useEffect(() => {
    fetchSalaries(selectedMonth, selectedYear);

    // Refetch when window gains focus
    const handleFocus = () => {
      fetchSalaries(selectedMonth, selectedYear);
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [selectedMonth, selectedYear]);

  const fetchSalaries = async (month, year) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const url = `${API_BASE_URL}/api/staff/salaries-by-month?month=${month}&year=${year}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setSalaryData(data.staffSalaries || []);
      } else {
        const errorText = await response.text();
        toast.error("Failed to fetch salary data");
      }
    } catch (error) {
      toast.error("Failed to load salary data");
    } finally {
      setLoading(false);
    }
  };

  const handleMonthYearChange = (event) => {
    const value = event.target.value; // Format: "YYYY-MM"
    const [year, month] = value.split('-');
    setSelectedYear(year);
    setSelectedMonth(parseInt(month, 10).toString());
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMonthName = (monthNum) => {
    const date = new Date(2000, monthNum - 1, 1);
    return date.toLocaleDateString("en-IN", { month: "long" });
  };

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ 
        p: { xs: 2, md: 4 },
        bgcolor: "#0a0a0a",
        minHeight: "100vh"
      }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={() => navigate("/staff")}
              sx={{
                mr: 2,
                bgcolor: "#C4A574",
                color: "#000",
                "&:hover": { bgcolor: "#B39565" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#C4A574",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" }
              }}
            >
              Salary
            </Typography>
          </Box>
          
          {/* Month/Year Picker */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarTodayIcon sx={{ color: "#C4A574" }} />
            <TextField
              type="month"
              value={`${selectedYear}-${selectedMonth.padStart(2, '0')}`}
              onChange={handleMonthYearChange}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#2a2a2a",
                  color: "#C4A574",
                  "& fieldset": {
                    borderColor: "#C4A574",
                    borderWidth: 2,
                  },
                  "&:hover fieldset": {
                    borderColor: "#B39565",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#C4A574",
                  },
                  "& input": {
                    color: "#C4A574",
                    fontWeight: 600,
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Selected Month Display */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: "#C4A574",
              fontWeight: 600
            }}
          >
            {getMonthName(selectedMonth)} {selectedYear}
          </Typography>
        </Box>

        {/* Salary Cards Grid */}
        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {salaryData.length === 0 ? (
            <Grid item xs={12}>
              <Typography align="center" sx={{ color: "#888", py: 4 }}>
                No active staff found for this month
              </Typography>
            </Grid>
          ) : (
            salaryData.map((staff) => {
              const initials = getInitials(staff.fullName);
              const avatarColor = getAvatarColor(staff.fullName);

              return (
                <Grid item key={staff.staffId} xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#1a1a1a",
                      border: "2px solid #333",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                        borderColor: "#C4A574",
                      },
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                      {/* Staff Info */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Avatar
                          src={staff.photo ? `${API_BASE_URL}${staff.photo}` : undefined}
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: staff.photo ? 'transparent' : avatarColor,
                            mr: 2,
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            border: '2px solid #C4A574',
                            '& img': {
                              objectFit: 'cover'
                            }
                          }}
                        >
                          {!staff.photo && initials}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: "#fff" }}
                          >
                            {staff.fullName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#C4A574" }}>
                            {staff.designation}
                          </Typography>
                          <Chip
                            label={staff.staffType}
                            size="small"
                            sx={{
                              mt: 0.5,
                              bgcolor: staff.staffType === 'Hourly' ? '#1976D2' : '#FF9800',
                              color: "white",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Salary Details */}
                      <Box sx={{ bgcolor: "#2a2a2a", p: 2, borderRadius: 1, mb: 2, border: "1px solid #333" }}>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#888" }}
                            >
                              Monthly Salary
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#fff" }}
                            >
                              {formatCurrency(staff.monthlySalary)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#888" }}
                            >
                              Calculated Salary
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, color: "#4CAF50" }}
                            >
                              {formatCurrency(staff.staffType === 'Task-Based' ? staff.taskEarnings : staff.calculatedSalary)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Summary Box - Different for Task-Based vs Hourly */}
                      <Box sx={{ bgcolor: "#333", p: 2, borderRadius: 1, mb: 2, border: "1px solid #444" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: "#C4A574" }}
                        >
                          {staff.staffType === 'Task-Based' ? 'Earnings Breakdown' : 'Attendance Summary'}
                        </Typography>
                        <Grid container spacing={1}>
                          {staff.staffType === 'Task-Based' ? (
                            <>
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: "#888" }}>
                                  Base Salary
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#fff" }}>
                                  {formatCurrency(staff.monthlySalary)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: "#888" }}>
                                  Task Earnings
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1976D2" }}>
                                  {formatCurrency(staff.taskEarnings || 0)}
                                </Typography>
                              </Grid>
                            </>
                          ) : (
                            <>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "#888" }}>
                                  Present
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#4CAF50" }}>
                                  {staff.presentDays} days
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "#888" }}>
                                  Absent
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#F44336" }}>
                                  {staff.absentDays} days
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: "#888" }}>
                                  Total Work Hours
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1976D2" }}>
                                  {staff.totalWorkHours} hours
                                </Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          )}
        </Grid>
      </Box>
    </AppLayout>
  );
}
